import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { EVENT } from '../constants';
import { ApiButtonEvent, ApiErrorEvent, ApiPageEvent, ButtonEvent, ErrorEvent, PageEvent } from '../interfaces';
import { mapButtonEvent, mapErrorEvent, mapPageEvent } from '../mappers';
import { EventItemType } from '../types';
import { EnvironmentService, STATE, StateType } from '@unive/core';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly analyticsUrl!: string;
  private readonly analyticsEnabled!: boolean;
  private state: StateType = STATE.Initial;
  private queue: EventItemType[] = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    environmentService: EnvironmentService,
  ) {
    this.analyticsUrl = environmentService.getEnvironment().analyticsUrl;
    this.analyticsEnabled = !!environmentService.getEnvironment().enableAnalytics;

    if (this.analyticsEnabled) {
      this.loadScript(this.analyticsUrl);
    }
  }

  public trackPage(event: PageEvent): void {
    if (!this.analyticsEnabled) {
      return;
    }
    const apiEvent: ApiPageEvent = mapPageEvent(event);
    this.track([EVENT.PageView, apiEvent]);
  }

  public trackButton(event: ButtonEvent): void {
    if (!this.analyticsEnabled) {
      return;
    }
    const apiEvent: ApiButtonEvent = mapButtonEvent(event);
    this.track([EVENT.Button, apiEvent]);
  }

  public trackError(event: ErrorEvent): void {
    if (!this.analyticsEnabled) {
      return;
    }
    const apiEvent: ApiErrorEvent = mapErrorEvent(event);
    this.track([EVENT.Error, apiEvent]);
  }

  private loadScript(url: string): void {
    this.state = STATE.Pending;
    const renderer: Renderer2 = this.rendererFactory.createRenderer(document, null);
    const script: HTMLScriptElement = renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = url;

    script.onload = (): void => {
      this.state = STATE.Success;
      this.processQueue();
    };
    renderer.appendChild(this.document.body, script);
  }

  private processQueue(): void {
    this.queue.forEach((eventItem: EventItemType): void => this.track(eventItem));
  }

  private track(eventItem: EventItemType): void {
    this.state !== STATE.Success ? this.queue.push(eventItem) : window.adv_event.push(eventItem);
  }
}
